import { THEME_MODE_STORAGE_KEY } from '../constants/storage';

export type ResolvedTheme = 'light' | 'dark';
export type ThemeMode = ResolvedTheme | 'system';

const THEME_MODES = ['light', 'dark', 'system'] as const;
const SYSTEM_THEME_QUERY = '(prefers-color-scheme: dark)';

export function isThemeMode(value: string | null): value is ThemeMode {
  return THEME_MODES.includes(value as ThemeMode);
}

export function getSystemTheme(): ResolvedTheme {
  if (!window.matchMedia) {
    return 'light';
  }

  return window.matchMedia(SYSTEM_THEME_QUERY).matches ? 'dark' : 'light';
}

export function getSystemThemeMediaQuery() {
  return window.matchMedia?.(SYSTEM_THEME_QUERY) ?? null;
}

export function getStoredThemeMode(): ThemeMode {
  const storedThemeMode = window.localStorage.getItem(THEME_MODE_STORAGE_KEY);

  if (storedThemeMode == null) {
    return 'system';
  }

  if (!isThemeMode(storedThemeMode)) {
    console.warn(
      `Ignoring invalid theme mode "${storedThemeMode}" from local storage.`,
    );
    window.localStorage.removeItem(THEME_MODE_STORAGE_KEY);
    return 'system';
  }

  return storedThemeMode;
}

export function persistThemeMode(themeMode: ThemeMode) {
  if (themeMode === 'system') {
    window.localStorage.removeItem(THEME_MODE_STORAGE_KEY);
    return;
  }

  window.localStorage.setItem(THEME_MODE_STORAGE_KEY, themeMode);
}

export function resolveThemeMode(themeMode: ThemeMode): ResolvedTheme {
  return themeMode === 'system' ? getSystemTheme() : themeMode;
}

export function applyResolvedTheme(resolvedTheme: ResolvedTheme) {
  const root = window.document.documentElement;

  root.classList.toggle('dark', resolvedTheme === 'dark');
  root.dataset.theme = resolvedTheme;
  root.style.colorScheme = resolvedTheme;
}

export function applyInitialThemePreference() {
  applyResolvedTheme(resolveThemeMode(getStoredThemeMode()));
}
